import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import {
  getBookingFieldSettings,
  getEnquiryFieldSettings,
} from "../../selectors/modules.js";
import { selectActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/offerings-module.module.css";
import {
  APIOffering,
  AspectRatio,
  BaseModuleProps,
  ColorScheme,
  OfferingsModuleAspectRatio,
  OfferingsModuleSettings,
  OfferingsType,
  OfferingsWidgetSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
  isDefined,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import OfferingsWidget from "../OfferingsWidget.js";
import Spinner from "../Spinner.js";

type Props = BaseModuleProps<OfferingsModuleSettings> & {
  type: OfferingsType;
  urlHashPrefix: string;
  scheme?: ColorScheme;
};

const getOfferingIdFromHash = (urlHashPrefix: string): number | undefined => {
  // For server side rendering
  if (typeof location === "undefined") return undefined;
  const [, hashId = ""] = location.hash.split(urlHashPrefix);
  const offeringId = +hashId;
  return isNaN(offeringId) ? undefined : offeringId;
};

type OfferingQueryParam = "roomId" | "offerId";

const offeringParamsMap: {
  [offeringsType in OfferingsType]: OfferingQueryParam;
} = { Rooms: "roomId", Specials: "offerId" };

const getOfferingIdFromQuery = (
  offeringsType: OfferingsType,
  moduleId: string,
): number | undefined => {
  if (typeof location === "undefined") return undefined;
  const key = offeringParamsMap[offeringsType];
  const searchParams = new URLSearchParams(window.location.search);

  const query = {
    roomId: searchParams.get("roomId") ?? undefined,
    offerId: searchParams.get("offerId") ?? undefined,
    moduleId: searchParams.get("moduleId") ?? undefined,
  };
  const offeringId = query[key];
  return query.moduleId !== moduleId ||
    offeringId === undefined ||
    isNaN(+offeringId)
    ? undefined
    : +offeringId;
};

const filterOfferings = ({
  pinnedItems,
  maxItems,
  offerings,
}: {
  pinnedItems: number[];
  maxItems: number | undefined;
  offerings: APIOffering[];
}): APIOffering[] => {
  if (!pinnedItems.length) {
    return offerings.slice(0, maxItems || undefined);
  }

  const byId = offerings.reduce<{ [key in number]?: APIOffering }>(
    (acc, offering) => {
      acc[offering.id] = offering;
      return acc;
    },
    {},
  );

  const allIds = offerings.map(({ id }) => id);

  const idsNotInPinnedItems = allIds.filter(
    (offeringId) => pinnedItems.indexOf(offeringId) === -1,
  );

  const orderedIds = [
    ...pinnedItems,
    ...(pinnedItems.length ? idsNotInPinnedItems : []),
  ];

  const filteredOfferings = orderedIds
    .map((id) => byId[id])
    .filter(isDefined)
    .slice(0, maxItems ?? undefined);

  return filteredOfferings;
};

const OfferingModule: FunctionComponent<Props> = ({
  isPreview,
  isFirstOnPage,
  scheme: parentScheme,
  translatedModule: {
    id: moduleId,
    parentId,
    translation: {
      languageId,
      settings: { title, subtitle },
    },
    settings: {
      textAlign,
      pinnedItems,
      maxItems,
      displayType,
      mediaAspectRatio,
      layout,
      maxColumns,
      bookingLayout,
      enquiryLayout,
      themeIds,
    },
  },
  translatedModule,
  type,
  urlHashPrefix,
}) => {
  const site = useAppSelector(selectActiveSite);
  const scheme = useAppSelector(
    (state) =>
      parentScheme ??
      getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const modules = useAppSelector((state) => state.modules);
  const enquiryFieldSettings = getEnquiryFieldSettings(modules, languageId);
  const bookingFieldSettings = getBookingFieldSettings(modules, languageId);

  const isInsidePopUpModule = useAppSelector((state) =>
    parentId ? state.modules.byId[parentId]?.type === "PopUpModule" : false,
  );

  const offeringsWidgetSettings = {
    colorScheme: scheme,
    propertyId: site.accommodation.id,
    imageAspectRatio: aspectRatioMapping[mediaAspectRatio],
    layout,
    displayType: isInsidePopUpModule ? "slides" : displayType,
    lang: getSupportedLanguage(languageId, ["de", "it", "en", "fr"]),
    themeIds,
    maxColumns: isInsidePopUpModule ? 1 : maxColumns,
    bookingSettings: {
      layout: isInsidePopUpModule ? "four_steps" : bookingLayout,
      fieldSettings: bookingFieldSettings,
    },
    enquirySettings: {
      layout: isInsidePopUpModule ? "layout_1" : enquiryLayout,
      fieldSettings: enquiryFieldSettings,
    },
  };

  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const [hasOfferings, setHasOfferings] = useState(true);
  const offeringIdFromQuery = useRef(
    getOfferingIdFromQuery(type, moduleId),
  ).current;
  const offeringIdFromHash = useRef(
    getOfferingIdFromHash(urlHashPrefix),
  ).current;
  const offeringIdFromUrl = offeringIdFromQuery ?? offeringIdFromHash;

  const onWidgetLoad: OfferingsWidgetSettings["onLoad"] = (
    { openDetails },
    offerings,
  ) => {
    setIsWidgetLoaded(true);
    offeringIdFromUrl && openDetails(offeringIdFromUrl);

    const filteredOfferings = filterOfferings({
      maxItems,
      pinnedItems,
      offerings,
    });

    setHasOfferings(!!filteredOfferings.length);

    return filteredOfferings;
  };

  const onDetailsOpen: OfferingsWidgetSettings["onDetailsOpen"] = ({ id }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("moduleId", moduleId);
    searchParams.set(offeringParamsMap[type], String(id));

    history.pushState(
      null,
      "",
      `${location.pathname}?${searchParams.toString()}`,
    );
  };

  const onDetailsClose: OfferingsWidgetSettings["onDetailsClose"] = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(offeringParamsMap[type]);
    searchParams.delete("moduleId");
    const searchString = searchParams.toString();

    history.pushState(
      null,
      "",
      `${location.pathname}${searchString ? "?" : ""}${searchString}`,
    );
  };

  // Hide the whole module on generated site if there’s no special
  if (!isPreview && !hasOfferings) return null;

  return (
    <ModuleWithHeadings
      id={moduleId}
      colors={{ background: scheme.main.separator }}
      className={styles.OfferingsModule}
      title={title}
      subtitle={subtitle}
    >
      {!isWidgetLoaded && offeringIdFromUrl !== undefined && (
        <div className={styles.OfferingsModule__DetailsLoading}>
          <Spinner />
        </div>
      )}
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>
      <div
        className={ClassNames({
          [moduleBaseStyles.Module__Wrapper]: !(
            offeringsWidgetSettings.layout === "layout_4"
          ),
        })}
      >
        <OfferingsWidget
          type={type}
          lazyload={offeringIdFromUrl === undefined}
          pinnedItems={pinnedItems}
          maxItems={maxItems}
          settings={{
            ...offeringsWidgetSettings,
            onLoad: onWidgetLoad,
            onDetailsOpen,
            onDetailsClose,
            onError: (e) => {
              console.error("[ERROR]", e);
              setIsWidgetLoaded(true);
            },
          }}
        />
      </div>
    </ModuleWithHeadings>
  );
};

const aspectRatioMapping: { [key in AspectRatio]: OfferingsModuleAspectRatio } =
  {
    4: 0.25,
    2.6667: 0.375,
    1.7778: 0.5625,
    1.5: 0.6667,
    1.3333: 0.75,
    1: 1,
    0.75: 1.3333,
    0.6667: 1.5,
    0.5625: 1.7778,
    0.375: 2.6667,
  };

export default OfferingModule;
